// The in-portal route from the free month to the 3-month trial.
//
// Rendered as an overlay by DemoBoot rather than from inside App.js, so the
// portal source needs no knowledge of any of this — its only licensing change
// is asking planGate whether a module is unlocked.
//
// Two steps, because the point of the exercise is a qualified lead: the form
// collects who they are and which hotel they are from, and the code proves the
// WORK address they typed is real. Their Google address is already verified,
// but it is usually personal — asking again is what turns a click into a contact.

import { useState } from "react";
import { confirmTrial, requestTrial } from "./licenseService";
import { LOCKED_MODULES } from "./plans";

const C = { ink: "#16122b", muted: "#625d7a", violet: "#6248e0", coral: "#ff8257" };

const overlay = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  overflowY: "auto",
  fontFamily: "Inter, sans-serif",
  background: "rgba(14,10,30,.62)",
  backdropFilter: "blur(4px)",
};

const card = {
  width: "100%",
  maxWidth: 520,
  borderRadius: 24,
  background: "#fff",
  boxShadow: "0 30px 70px -20px rgba(23,18,46,.6)",
  overflow: "hidden",
  maxHeight: "92vh",
  display: "flex",
  flexDirection: "column",
};

const header = {
  padding: "26px 30px 22px",
  color: "#fff",
  background: "linear-gradient(135deg,#4a32bd,#6248e0 55%,#9b86ff)",
};

const body = { padding: "24px 30px 26px", overflowY: "auto" };

const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid #ddd5fa",
  background: "#f8f7fd",
  fontSize: 15,
  color: C.ink,
  outline: "none",
  marginBottom: 10,
};

const primary = (busy) => ({
  width: "100%",
  padding: "14px 22px",
  borderRadius: 999,
  border: 0,
  cursor: busy ? "default" : "pointer",
  opacity: busy ? 0.65 : 1,
  fontSize: 15,
  fontWeight: 700,
  color: "#fff",
  background: "linear-gradient(90deg,#4a32bd,#6248e0 55%,#9b86ff)",
});

const link = {
  background: "none",
  border: 0,
  padding: 0,
  color: C.muted,
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
};

export default function DemoUpgrade({ user, db, daysLeft, onClose }) {
  const [step, setStep] = useState("form"); // form | code | done
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "" });
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submitForm(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await requestTrial(user, form);
      if (r.alreadyTrial) {
        // Somebody upgraded in another tab; reloading picks up the licence.
        window.location.reload();
        return;
      }
      setSentTo(r.email || form.email);
      setStep("code");
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  }

  async function submitCode(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await confirmTrial(db, user, code.trim());
      setStep("done");
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setError("");
    try {
      await requestTrial(user, form);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  }

  return (
    <div style={overlay} onClick={step === "done" ? undefined : onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: "-.02em" }}>
            {step === "done" ? "You're all set" : "Unlock everything for 3 months"}
          </h2>
          <p style={{ margin: "7px 0 0", fontSize: 14, opacity: 0.92, lineHeight: 1.6 }}>
            {step === "form" &&
              (daysLeft != null
                ? `Your free month has ${daysLeft} day${daysLeft === 1 ? "" : "s"} left. Register to extend it to three months and open the management modules.`
                : "Register to extend your demo to three months and open the management modules.")}
            {step === "code" && `We sent a 6-digit code to ${sentTo}.`}
            {step === "done" && "Your 3-month trial is active. Everything is unlocked."}
          </p>
        </div>

        <div style={body}>
          {step === "form" && (
            <>
              <div style={{ marginBottom: 20 }}>
                {LOCKED_MODULES.map((m) => (
                  <div key={m.feature} style={{ display: "flex", gap: 10, marginBottom: 11 }}>
                    <span style={{ color: C.violet, fontWeight: 700, lineHeight: 1.5 }}>+</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{m.label}</div>
                      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{m.blurb}</div>
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={submitForm}>
                <input style={input} required placeholder="Your name" value={form.name} onChange={set("name")} />
                <input style={input} required placeholder="Hotel / company" value={form.company} onChange={set("company")} />
                <input style={input} required type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} />
                <input style={input} required type="email" placeholder="Work email" value={form.email} onChange={set("email")} />
                <p style={{ margin: "2px 0 14px", fontSize: 12, color: "#8b86a0", lineHeight: 1.5 }}>
                  We send the confirmation code to this address, so it needs to be one you can open.
                </p>
                {error && <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "#c0392b" }} role="alert">{error}</p>}
                <button type="submit" disabled={busy} style={primary(busy)}>
                  {busy ? "Sending…" : "Send me the code"}
                </button>
              </form>
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={onClose} style={link}>Not now</button>
              </div>
            </>
          )}

          {step === "code" && (
            <form onSubmit={submitCode}>
              <input
                style={{ ...input, fontSize: 26, letterSpacing: ".38em", textAlign: "center", fontWeight: 700 }}
                required
                inputMode="numeric"
                pattern="\\d{6}"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\\D/g, ""))}
              />
              <p style={{ margin: "2px 0 14px", fontSize: 12.5, color: "#8b86a0", lineHeight: 1.5 }}>
                The code expires in 30 minutes. Check your spam folder if it hasn't arrived.
              </p>
              {error && <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "#c0392b" }} role="alert">{error}</p>}
              <button type="submit" disabled={busy} style={primary(busy)}>
                {busy ? "Checking…" : "Start my 3-month trial"}
              </button>
              <div style={{ display: "flex", justifyContent: "center", gap: 18, marginTop: 14 }}>
                <button type="button" onClick={resend} disabled={busy} style={{ ...link, color: C.violet }}>
                  Resend code
                </button>
                <button type="button" onClick={() => { setStep("form"); setError(""); }} disabled={busy} style={link}>
                  Change details
                </button>
              </div>
            </form>
          )}

          {step === "done" && (
            <>
              <p style={{ margin: "0 0 20px", fontSize: 14.5, lineHeight: 1.7, color: C.muted }}>
                The Daily Operations Report, Staff Accommodation, the Employee of the Month
                survey and the staff administration tools are now open. Everything you have
                already set up is exactly where you left it.
              </p>
              <button type="button" onClick={() => window.location.reload()} style={primary(false)}>
                Open the full portal
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
